/**
 * "This browser has already asked for a trial."
 *
 * ⚠️ **Deliberately a soft block, and it must never be described as anything more.** It is a
 * `localStorage` flag: clearing site data removes it, a private window never has it, and a script
 * never sets it in the first place. What it actually prevents is a *person* starting a second trial
 * by accident or by impatience — refreshing, assuming the first attempt failed, filling the form
 * again — which is the common case and the one worth handling.
 *
 * It is **not** the abuse control. That is Turnstile on the same form (automation), the
 * business-email rule (throwaway addresses), and `customer_start_trial`'s one-live-trial-per-owner
 * check in the database (accumulation). Treating this as a defence would be believing a note on the
 * door is a lock.
 *
 * ⚠️ **It never blocks, only tells.** Someone who genuinely needs a second trial — a colleague on a
 * shared machine, a real second organisation — can proceed. A hard block here would produce a
 * support ticket for a rule that anyone determined can step around in two clicks anyway.
 */

const KEY = 'dedupr.trial-requested';

/** Storage can throw (Safari private mode, disabled cookies). An absent marker is the safe answer. */
export function hasRequestedTrial(): boolean {
  try {
    return window.localStorage.getItem(KEY) !== null;
  } catch {
    return false;
  }
}

export function markTrialRequested(): void {
  try {
    // The timestamp is for us reading a support conversation, not for any logic here.
    window.localStorage.setItem(KEY, new Date().toISOString());
  } catch {
    /* Refused storage is not a failure worth reporting: the signup succeeded, and this marker is a
       convenience. Failing the flow over it would be the tail wagging the dog. */
  }
}
